import { SearchContext } from "src/search/useSearchContext";
import styles from "./Tooltip.module.css";
import { CustomTooltip } from "src/components/CustomTooltip";
import { TooltipContentModule, TooltipContext } from "./tooltip-types";
import { valueToNumber } from "../common";

interface HexagridCellData {
  label: string;
  value: { value: string; label: string };
  values: { value: string; label: string }[];
}

interface HexagridCellTooltipProps {
  cell: HexagridCellData;
  context: SearchContext;
  min: number;
  max: number;
  total: number;
  children: React.ReactNode;
}

export const HexagridTooltipModule: TooltipContentModule = {
  type: "group",
  priority: 1,
  shouldRender: (context: TooltipContext) => {
    return context.type === "group";
  },
  render: (context: TooltipContext) => {
    if (!context.record) return null;
    const aggregation = context.aggregations?.[0];
    const cell = context.record as HexagridCellData;

    // Calculate total and percentage
    const cellValue = Number(valueToNumber(cell.value.value));
    const percentage =
      context.metadata?.total > 0
        ? (cellValue / context.metadata?.total) * 100
        : 0;

    // Get min/max from context if available
    const min = context.metadata?.min;
    const max = context.metadata?.max;
    const range = min !== undefined && max !== undefined ? max - min : 0;
    const positionInRange =
      min !== undefined && range > 0 ? ((cellValue - min) / range) * 100 : 0;

    return (
      <div className={styles.tooltip_container}>
        <div className={styles.tooltip_header}>Cell Information</div>
        <div>
          <div className={styles.tooltip_section}>
            <div className={styles.tooltip_row}>
              <div className={styles.tooltip_label}>Label</div>
              <span className={styles.tooltip_value}>{cell.label}</span>
            </div>
            {aggregation && (
              <>
                <div className={styles.tooltip_row}>
                  <div className={styles.tooltip_label}>Measure</div>
                  <span className={styles.tooltip_value}>
                    {aggregation.field}
                  </span>
                </div>
                <div className={styles.tooltip_row}>
                  <div className={styles.tooltip_label}>Operator</div>
                  <span className={styles.tooltip_value}>
                    {aggregation.reducer}
                  </span>
                </div>
              </>
            )}
            <div className={styles.tooltip_row}>
              <div className={styles.tooltip_label}>Value</div>
              <span className={styles.tooltip_value}>{cell.value.label}</span>
            </div>
            <div className={styles.tooltip_row}>
              <div className={styles.tooltip_label}>% of Total</div>
              <span className={styles.tooltip_value}>
                {percentage.toFixed(1)}%
              </span>
            </div>
            {min !== undefined && max !== undefined && (
              <>
                <div className={styles.tooltip_row}>
                  <div className={styles.tooltip_label}>Percentile</div>
                  <span className={styles.tooltip_value}>
                    {positionInRange.toFixed(1)}%
                  </span>
                </div>
                <div className={styles.tooltip_row}>
                  <div className={styles.tooltip_label}>Value Range</div>
                  <span className={styles.tooltip_value}>
                    {min.toLocaleString()} - {max.toLocaleString()}
                  </span>
                </div>
              </>
            )}
            {cell.values && cell.values.length > 0 && (
              <div className={styles.tooltip_row}>
                <div className={styles.tooltip_label}>Group Values</div>
                <span className={styles.tooltip_value}>
                  {cell.values.length}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
};

export function HexagridCellTooltip({
  cell,
  context,
  min,
  max,
  total,
  children,
}: HexagridCellTooltipProps) {
  const { params } = context;
  const aggregation = params.state.aggregations?.[0];

  // Calculate min/max for the context
  const values =
    context.aggregations.data?.data?.map((d) => {
      if (!d?.value) return 0;
      const numValue = Number(valueToNumber(d.value.toString()));
      return Number.isNaN(numValue) ? 0 : numValue;
    }) || [];

  const tooltipContext: TooltipContext = {
    type: "group",
    value: cell.value.value,
    formattedValue: cell.value.label,
    record: cell,
    currentColumn: aggregation,
    metadata: {
      level: 0,
      path: [],
      isLeaf: true,
      depth: 0,
      min,
      max,
      total,
    },
    aggregations: [aggregation].filter(Boolean),
    groupBys: [],
    children: null,
  };

  return (
    <CustomTooltip
      content={HexagridTooltipModule.render(tooltipContext)}
      placement="right"
      size="medium"
      overlayStyle={{ maxWidth: 400 }}
    >
      {children}
    </CustomTooltip>
  );
}
